import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import AdminHeader from './AdminHeader';
import { teamManagerService } from '../services/teamManagerService';
import { COUNTRIES, getCountryByName, getCountryNameFromCodes, getCountryFlagUrl } from '../data/countries';
import RelativeTimePill from './RelativeTimePill';

const AdminTeamManagers = () => {
    const [managers, setManagers] = useState([]);
    const [counts, setCounts] = useState({});
    const [overview, setOverview] = useState({ total: 0, withPhoto: 0, lastSyncedAt: null });
    const [loading, setLoading] = useState(true);
    const [syncingCodes, setSyncingCodes] = useState(new Set());

    const [modalOpen, setModalOpen] = useState(false);
    const [formEmail, setFormEmail] = useState('');
    const [formCountryName, setFormCountryName] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');

    // Mirrors `managers` so async callbacks (e.g., refreshOverview after a sync that
    // ran while a manager was being added) read the latest list rather than the
    // value captured when the closure was created.
    const managersRef = useRef([]);
    useEffect(() => { managersRef.current = managers; }, [managers]);

    const refreshCounts = useCallback(async (mgrs) => {
        try {
            const codes = mgrs.flatMap(m => [m.nationality_iso3, m.nationality_ioc]);
            const c = await teamManagerService.countPerNationality(codes);
            setCounts(c);
        } catch (err) {
            console.error('Failed to fetch counts', err);
        }
    }, []);

    const refreshOverview = useCallback(async (mgrs) => {
        try {
            const codes = mgrs.flatMap(m => [m.nationality_iso3, m.nationality_ioc]);
            const stats = await teamManagerService.getOverviewStats(codes);
            setOverview(stats);
        } catch (err) {
            console.error('Failed to fetch overview', err);
        }
    }, []);

    const fetchManagers = useCallback(async () => {
        try {
            setLoading(true);
            const data = await teamManagerService.list();
            setManagers(data);
            refreshCounts(data);
            refreshOverview(data);
        } catch (err) {
            toast.error(err.message || 'Failed to load team managers');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [refreshCounts, refreshOverview]);

    useEffect(() => {
        fetchManagers();
    }, [fetchManagers]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (submitting) return;

        const country = getCountryByName(formCountryName);
        if (!country) {
            toast.error('Pick a country from the list');
            return;
        }

        setSubmitting(true);
        try {
            const created = await teamManagerService.create({
                email: formEmail,
                nationality_iso3: country.iso3,
                nationality_ioc: country.ioc,
            });
            toast.success(`Team manager created for ${country.name}`);
            setModalOpen(false);
            setFormEmail('');
            setFormCountryName('');
            await fetchManagers();

            // Auto-trigger initial sync for each code (parallel)
            const codes = [created.nationality_iso3, created.nationality_ioc].filter(Boolean);
            if (codes.length > 0) {
                triggerSync(codes, { silent: false, label: 'Initial sync' });
            }
        } catch (err) {
            toast.error(err.message || 'Failed to create team manager');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const triggerSync = async (codes, { silent = false, label = 'Sync' } = {}) => {
        const unique = [...new Set(codes.filter(Boolean))];
        if (unique.length === 0) return;

        // Mark all codes as syncing
        setSyncingCodes(prev => {
            const next = new Set(prev);
            unique.forEach(c => next.add(c));
            return next;
        });

        const toastId = silent ? null : toast.loading(`${label}: ${unique.join(', ')}...`);
        let totalUpserted = 0;
        const errors = [];

        // Sequential per-code (gentle on Sportradar)
        for (const code of unique) {
            try {
                const result = await teamManagerService.syncNationality(code);
                totalUpserted += result?.persons_upserted || 0;
            } catch (err) {
                console.error(`Sync failed for ${code}`, err);
                errors.push(`${code}: ${err.message || 'failed'}`);
            }
        }

        // Refresh count for those codes + the global overview
        try {
            const c = await teamManagerService.countPerNationality(unique);
            setCounts(prev => ({ ...prev, ...c }));
        } catch (err) {
            console.error('Failed to refresh counts after sync', err);
        }
        refreshOverview(managersRef.current);

        setSyncingCodes(prev => {
            const next = new Set(prev);
            unique.forEach(c => next.delete(c));
            return next;
        });

        if (toastId) toast.dismiss(toastId);
        if (errors.length === 0) {
            if (!silent) toast.success(`${label} complete: ${totalUpserted} persons upserted`);
        } else {
            toast.error(`${label} had errors: ${errors.join(' | ')}`);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await teamManagerService.remove(confirmDelete.id);
            toast.success('Team manager removed');
            setConfirmDelete(null);
            fetchManagers();
        } catch (err) {
            toast.error(err.message || 'Failed to delete');
            console.error(err);
        }
    };

    const trimmedSearch = searchQuery.trim().toLowerCase();
    const filteredManagers = managers.filter(m => {
        if (!trimmedSearch) return true;
        const countryName = getCountryNameFromCodes(m.nationality_iso3, m.nationality_ioc);
        return (
            (m.email || '').toLowerCase().includes(trimmedSearch) ||
            (m.nationality_iso3 || '').toLowerCase().includes(trimmedSearch) ||
            (m.nationality_ioc || '').toLowerCase().includes(trimmedSearch) ||
            countryName.toLowerCase().includes(trimmedSearch)
        );
    });

    const isCodeSyncing = (codes) => codes.some(c => c && syncingCodes.has(c));

    return (
        <div className="min-h-screen bg-gray-50">
            <Toaster position="top-right" />

            <AdminHeader currentPage="team-managers" />

            <div className="bg-white border-b shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">
                    {/* Title row */}
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                        <div>
                            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Team Managers</h1>
                            <p className="text-gray-500 mt-1">
                                Map team manager emails to nationality codes. Each manager can sign in via magic link
                                and see all Sportradar persons for their country.
                            </p>
                            {/* The sync cron runs on the 1st and 15th. pg_cron logs the
                                dispatch of net.http_post, not the sync's outcome, so a
                                failing job still reports "succeeded" — a mirror older
                                than one full period is the only reliable alarm. */}
                            <RelativeTimePill
                                date={overview.lastSyncedAt}
                                label="Last synced"
                                overdueAfterHours={16 * 24}
                                overdueLabel="sync may have failed"
                                className="mt-3"
                            />
                        </div>
                        {overview.total > 0 && (
                            <div className="hidden sm:flex flex-col items-end">
                                <div className="text-3xl font-black bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent tabular-nums">
                                    {Math.round((overview.withPhoto / overview.total) * 100)}%
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Coverage</div>
                            </div>
                        )}
                    </div>

                    {/* KPI tiles */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                        {/* Managers */}
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 sm:p-4 transition hover:shadow-sm">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="w-2 h-2 rounded-full bg-indigo-500" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Managers</span>
                            </div>
                            <div className="text-2xl sm:text-3xl font-black text-gray-900 tabular-nums">
                                {managers.length.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-gray-400 mt-0.5">
                                {(() => {
                                    const n = new Set(managers.map(m => m.nationality_iso3).filter(Boolean)).size;
                                    return `Across ${n} ${n === 1 ? 'country' : 'countries'}`;
                                })()}
                            </div>
                        </div>

                        {/* Total persons */}
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 sm:p-4 transition hover:shadow-sm">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="w-2 h-2 rounded-full bg-gray-400" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Persons</span>
                            </div>
                            <div className="text-2xl sm:text-3xl font-black text-gray-900 tabular-nums">
                                {overview.total.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-gray-400 mt-0.5">Synced from Sportradar</div>
                        </div>

                        {/* With real photo (silhouettes excluded) */}
                        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-200/70 rounded-xl p-3 sm:p-4 transition hover:shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 left-0 bottom-0 w-1 bg-emerald-500" />
                            <div className="flex items-center gap-2 mb-1">
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">With photo</span>
                            </div>
                            <div className="text-2xl sm:text-3xl font-black text-emerald-700 tabular-nums">
                                {overview.withPhoto.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-emerald-600 mt-0.5 tabular-nums">
                                {overview.total > 0 ? `${Math.round((overview.withPhoto / overview.total) * 100)}%` : '0%'} of total
                            </div>
                        </div>

                        {/* Missing */}
                        <div className="bg-gradient-to-br from-amber-50 to-amber-100/40 border border-amber-200/70 rounded-xl p-3 sm:p-4 transition hover:shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 left-0 bottom-0 w-1 bg-amber-500" />
                            <div className="flex items-center gap-2 mb-1">
                                <span className="w-2 h-2 rounded-full bg-amber-500" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Missing</span>
                            </div>
                            <div className="text-2xl sm:text-3xl font-black text-amber-700 tabular-nums">
                                {Math.max(0, overview.total - overview.withPhoto).toLocaleString()}
                            </div>
                            <div className="text-[11px] text-amber-600 mt-0.5 tabular-nums">
                                {overview.total > 0 ? `${Math.round(((overview.total - overview.withPhoto) / overview.total) * 100)}%` : '0%'} of total
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-6 py-8">
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold text-gray-800">
                            Mappings <span className="text-gray-400 text-base font-medium">({managers.length})</span>
                        </h2>
                        <button
                            onClick={() => setModalOpen(true)}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                        >
                            + Add Team Manager
                        </button>
                    </div>

                    {/* Search */}
                    <div className="relative mb-4">
                        <input
                            type="text"
                            placeholder="Search by email or country..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                        />
                        <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                aria-label="Clear search"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="py-12 text-center text-gray-500">Loading...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                                    <tr>
                                        <th className="px-4 py-3">Email</th>
                                        <th className="px-4 py-3">Country</th>
                                        <th className="px-4 py-3 text-center">Persons</th>
                                        <th className="px-4 py-3">Created</th>
                                        <th className="px-4 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {managers.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                                                <p className="text-sm">No team managers yet.</p>
                                                <button
                                                    onClick={() => setModalOpen(true)}
                                                    className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                                                >
                                                    Add the first one
                                                </button>
                                            </td>
                                        </tr>
                                    ) : filteredManagers.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                                                <p className="text-sm">No matches.</p>
                                                <button
                                                    onClick={() => setSearchQuery('')}
                                                    className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                                                >
                                                    Clear search
                                                </button>
                                            </td>
                                        </tr>
                                    ) : filteredManagers.map(m => {
                                        const codes = [m.nationality_iso3, m.nationality_ioc].filter(Boolean);
                                        // Dedupe codes — when iso3 === ioc (e.g., India 'IND'), summing both
                                        // would double-count the same rows.
                                        const uniqueCodes = [...new Set(codes)];
                                        const totalCount = uniqueCodes.reduce((sum, c) => sum + (counts[c] ?? 0), 0);
                                        const syncing = isCodeSyncing(codes);
                                        const countryName = getCountryNameFromCodes(m.nationality_iso3, m.nationality_ioc);
                                        const flagUrl = getCountryFlagUrl(m.nationality_iso3, m.nationality_ioc, 40);
                                        return (
                                            <tr key={m.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm">{m.email}</td>
                                                <td className="px-4 py-3">
                                                    <span className="inline-flex items-center gap-2 font-semibold text-gray-900 text-sm">
                                                        {flagUrl && (
                                                            <img
                                                                src={flagUrl}
                                                                alt=""
                                                                className="w-6 h-4 object-cover rounded-sm shadow-sm border border-gray-200"
                                                                onError={e => { e.currentTarget.style.display = 'none'; }}
                                                            />
                                                        )}
                                                        {countryName || '—'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700 text-center tabular-nums">
                                                    {totalCount.toLocaleString()}
                                                </td>
                                                <td className="px-4 py-3 text-gray-500 text-sm">
                                                    {new Date(m.created_at).toLocaleDateString()}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-3 items-center">
                                                        <button
                                                            onClick={() => triggerSync(codes, { label: 'Sync' })}
                                                            disabled={syncing}
                                                            className="text-sm font-medium text-indigo-600 hover:text-indigo-900 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
                                                        >
                                                            {syncing ? (
                                                                <>
                                                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                                    </svg>
                                                                    Syncing...
                                                                </>
                                                            ) : (
                                                                'Sync now'
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => setConfirmDelete(m)}
                                                            className="text-sm font-medium text-red-600 hover:text-red-800"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>

            {/* Create Modal */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg w-full max-w-md p-6">
                        <h3 className="text-lg font-bold mb-1">Add Team Manager</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Pick a country and enter the manager's email address.
                        </p>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email" required
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={formEmail}
                                    onChange={e => setFormEmail(e.target.value)}
                                    placeholder="manager@team.org"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                <input
                                    type="text" required
                                    list="tm-country-list"
                                    autoComplete="off"
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={formCountryName}
                                    onChange={e => setFormCountryName(e.target.value)}
                                    placeholder="Start typing... e.g., Malaysia"
                                />
                                <datalist id="tm-country-list">
                                    {COUNTRIES.map(c => (
                                        <option key={c.iso3} value={c.name} />
                                    ))}
                                </datalist>
                                {formCountryName && (() => {
                                    const c = getCountryByName(formCountryName);
                                    if (c) {
                                        const flagUrl = getCountryFlagUrl(c.iso3, c.ioc, 40);
                                        return (
                                            <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1.5">
                                                {flagUrl && (
                                                    <img
                                                        src={flagUrl}
                                                        alt=""
                                                        className="w-5 h-3 object-cover rounded-sm border border-gray-200"
                                                        onError={e => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                )}
                                                <span>Will register manager for <span className="font-semibold">{c.name}</span>.</span>
                                            </p>
                                        );
                                    }
                                    return (
                                        <p className="text-xs text-amber-700 mt-1">
                                            Pick a country from the list to continue.
                                        </p>
                                    );
                                })()}
                            </div>
                            <p className="text-xs text-gray-500">
                                After creation, an initial Sportradar sync runs automatically for this country.
                            </p>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    disabled={submitting}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    {submitting ? 'Creating...' : 'Create + Sync'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {confirmDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg w-full max-w-md p-6">
                        <h3 className="text-lg font-bold mb-2">Remove Team Manager?</h3>
                        <p className="text-sm text-gray-700 mb-4">
                            This will revoke access for <strong>{confirmDelete.email}</strong>. The
                            Sportradar person data is not deleted.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTeamManagers;
